import { useEffect, useState } from "react";
import { api, extractApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Pill from "@/components/Pill";
import ConfirmDialog from "@/components/ConfirmDialog";
import SecretDialog from "@/components/SecretDialog";
import ResponsiveDataView from "@/components/responsive/ResponsiveDataView";
import ResponsivePageHeader from "@/components/responsive/ResponsivePageHeader";
import { formatDateTime, useLanguage, useTranslation } from "@/i18n/LanguageContext";
import { Plus, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import usePaginatedResource from "@/hooks/usePaginatedResource";

export default function ApiKeys() {
  const t = useTranslation();
  const { language } = useLanguage();
  const [gyms, setGyms] = useState([]);
  const { page, loading, reload, changePage } = usePaginatedResource(
    async (params, { signal }) => (await api.get("/api-keys", { params, signal })).data,
    { onError: (err) => toast.error(extractApiError(err)) },
  );
  const [creating, setCreating] = useState(false);
  const [secret, setSecret] = useState(null);
  const [confirmRevoke, setConfirmRevoke] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    api.get("/gyms", { params: { limit: 100 }, signal: controller.signal })
      .then(({ data }) => setGyms(data.items || []))
      .catch((err) => {
        if (err?.name !== "CanceledError") toast.error(extractApiError(err));
      });
    return () => controller.abort();
  }, []);

  const create = async (values) => {
    try {
      const { data } = await api.post("/api-keys", values);
      setCreating(false);
      setSecret({ value: data.plaintextKey, name: data.apiKey.name });
      reload();
    } catch (err) {
      toast.error(extractApiError(err));
    }
  };

  const revoke = async (key) => {
    try {
      await api.delete(`/api-keys/${key.id}`);
      setConfirmRevoke(null);
      reload();
      toast.success(t("apiKeys.toast.revoked"));
    } catch (err) {
      toast.error(extractApiError(err));
    }
  };

  const gymById = Object.fromEntries(gyms.map((g) => [g.id, g.name]));
  const keyState = (key) => key.revokedAt
    ? <Pill variant="revoked">{t("common.states.revoked")}</Pill>
    : <Pill variant="active">{t("common.states.active")}</Pill>;
  const gymName = (key) => key.gymId ? gymById[key.gymId] || key.gymId : "-";

  return (
    <div data-testid="api-keys-page" className="space-y-6">
      <ResponsivePageHeader
        title={t("apiKeys.title")}
        description={t("apiKeys.description")}
        action={(
          <Button onClick={() => setCreating(true)} className="w-full rounded-lg sm:w-auto" data-testid="api-keys-create-button">
            <Plus className="mr-1 h-4 w-4" /> {t("apiKeys.newKey")}
          </Button>
        )}
      />

      <ResponsiveDataView
        items={page.items}
        loading={loading}
        emptyState={t("apiKeys.empty")}
        getRowKey={(k) => k.id}
        getRowTestId={(k) => `api-key-row-${k.id}`}
        tableTestId="api-keys-table"
        emptyTestId="api-keys-empty"
        columns={[
          { key: "name", header: t("apiKeys.columns.name"), cell: (k) => k.name },
          { key: "gym", header: t("apiKeys.columns.gym"), cell: (k) => <span className="text-muted-foreground">{gymName(k)}</span> },
          { key: "lastUsed", header: t("apiKeys.columns.lastUsed"), cell: (k) => <span className="font-mono text-[11px] text-muted-foreground">{formatDateTime(k.lastUsedAt, language)}</span> },
          { key: "state", header: t("apiKeys.columns.state"), cell: keyState },
          { key: "created", header: t("apiKeys.columns.created"), cell: (k) => <span className="font-mono text-[11px] text-muted-foreground">{formatDateTime(k.createdAt, language)}</span> },
        ]}
        mobileTitle={(k) => k.name}
        mobileSubtitle={(k) => gymName(k)}
        mobileBadge={keyState}
        mobileFields={[
          { label: t("common.fields.gym"), value: gymName },
          { label: t("common.fields.lastUsed"), value: (k) => formatDateTime(k.lastUsedAt, language) },
          { label: t("common.fields.created"), value: (k) => formatDateTime(k.createdAt, language), priority: "secondary" },
        ]}
        rowActions={[
          {
            key: "revoke",
            label: t("common.actions.revoke"),
            icon: ShieldOff,
            destructive: true,
            onClick: setConfirmRevoke,
            hidden: (k) => !!k.revokedAt,
            testId: (k) => `revoke-api-key-${k.id}`,
          },
        ]}
        pagination={{
          total: page.total,
          limit: page.limit,
          offset: page.offset,
          onChange: changePage,
          testId: "api-keys-pager",
        }}
      />

      <CreateKeyDialog open={creating} onClose={() => setCreating(false)} gyms={gyms} onCreate={create} />
      <ConfirmDialog
        open={!!confirmRevoke}
        onOpenChange={(o) => !o && setConfirmRevoke(null)}
        title={t("apiKeys.revoke.title")}
        description={t("apiKeys.revoke.description", { name: confirmRevoke?.name })}
        confirmLabel={t("common.actions.revoke")}
        destructive
        onConfirm={() => revoke(confirmRevoke)}
        testId="api-key-revoke-dialog"
      />
      <SecretDialog
        open={!!secret}
        onClose={() => setSecret(null)}
        title={t("apiKeys.secret.title")}
        description={t("apiKeys.secret.description", { name: secret?.name })}
        secret={secret?.value}
        testIdPrefix="api-key-secret"
      />
    </div>
  );
}

function CreateKeyDialog({ open, onClose, gyms, onCreate }) {
  const t = useTranslation();
  const [name, setName] = useState("");
  const [gymId, setGymId] = useState("");

  useEffect(() => {
    if (open) {
      setName("");
      setGymId("");
    }
  }, [open]);

  const submit = (e) => {
    e.preventDefault();
    onCreate({ name: name.trim(), gymId: gymId || null });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-border" data-testid="api-key-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">{t("apiKeys.create.title")}</DialogTitle>
          <DialogDescription className="text-xs">{t("apiKeys.plaintextShownOnce")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs">{t("common.fields.label")}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="rpi-device-1" data-testid="api-key-create-name" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">
              {t("apiKeys.create.scopeToGym")} <span className="text-muted-foreground">({t("common.misc.optional")})</span>
            </Label>
            <Select value={gymId || "__none"} onValueChange={(v) => setGymId(v === "__none" ? "" : v)}>
              <SelectTrigger data-testid="api-key-create-gym"><SelectValue placeholder={t("apiKeys.create.placeholder")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">{t("apiKeys.noGymOrgWide")}</SelectItem>
                {gyms.map((g) => (
                  <SelectItem key={g.id} value={g.id} data-testid={`api-key-create-gym-option-${g.id}`}>{g.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} className="rounded-lg" data-testid="api-key-create-cancel">{t("common.actions.cancel")}</Button>
            <Button type="submit" className="rounded-lg" data-testid="api-key-create-submit">{t("apiKeys.create.submit")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
